import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { TerminalStatus } from '@/types/api';
import type { PosTerminal } from '@/types/api';
import { X } from 'lucide-react';

interface RegisterTerminalModalProps {
  restaurantId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const EMPTY_REGISTER_FORM = {
  terminalId: '',
  name: '',
  location: '',
  ipAddress: '',
  macAddress: '',
  appVersion: '1.0.0',
  deviceModel: '',
};

export default function RegisterTerminalModal({ restaurantId, onClose, onSuccess }: RegisterTerminalModalProps) {
  const [formData, setFormData] = useState(EMPTY_REGISTER_FORM);
  const isDirty = JSON.stringify(formData) !== JSON.stringify(EMPTY_REGISTER_FORM);

  const handleClose = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDirty]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const registerMutation = useMutation({
    mutationFn: (data: Partial<PosTerminal>) => apiClient.registerTerminal(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to register terminal';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.terminalId.trim()) {
      newErrors.terminalId = 'Terminal ID is required';
    } else if (!/^[A-Z0-9-]+$/i.test(formData.terminalId)) {
      newErrors.terminalId = 'Terminal ID must be alphanumeric with dashes only';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (formData.ipAddress && !/^(\d{1,3}\.){3}\d{1,3}$/.test(formData.ipAddress)) {
      newErrors.ipAddress = 'Invalid IP address format';
    }

    if (formData.macAddress && !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(formData.macAddress)) {
      newErrors.macAddress = 'Invalid MAC address format (e.g., AA:BB:CC:DD:EE:FF)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    await registerMutation.mutateAsync({
      ...formData,
      restaurantId,
      status: TerminalStatus.OFFLINE,
      active: true,
    });
  };

  const generateTerminalId = () => {
    const id = `POS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    setFormData({ ...formData, terminalId: id });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="card max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Register New Terminal</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="terminal-id" className="block text-sm font-medium text-gray-700 mb-1">
              Terminal ID *
            </label>
            <div className="flex space-x-2">
              <input
                id="terminal-id"
                type="text"
                value={formData.terminalId}
                onChange={(e) => setFormData({ ...formData, terminalId: e.target.value.toUpperCase() })}
                className={`input flex-1 font-mono ${errors.terminalId ? 'border-red-500' : ''}`}
                placeholder="POS-XXXXX-XXXX"
              />
              <button
                type="button"
                onClick={generateTerminalId}
                className="btn btn-outline h-10 text-sm"
              >
                Generate
              </button>
            </div>
            {errors.terminalId && <p className="text-red-500 text-xs mt-1">{errors.terminalId}</p>}
          </div>

          <div>
            <label htmlFor="terminal-name" className="block text-sm font-medium text-gray-700 mb-1">
              Terminal Name *
            </label>
            <input
              id="terminal-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Main Bar POS, Front Counter"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="terminal-location" className="block text-sm font-medium text-gray-700 mb-1">
              Location *
            </label>
            <input
              id="terminal-location"
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className={`input w-full ${errors.location ? 'border-red-500' : ''}`}
              placeholder="e.g., Bar Area, Front Desk, Kitchen"
            />
            {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="terminal-ip" className="block text-sm font-medium text-gray-700 mb-1">
                IP Address
              </label>
              <input
                id="terminal-ip"
                type="text"
                value={formData.ipAddress}
                onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                className={`input w-full font-mono text-sm ${errors.ipAddress ? 'border-red-500' : ''}`}
                placeholder="192.168.1.100"
              />
              {errors.ipAddress && <p className="text-red-500 text-xs mt-1">{errors.ipAddress}</p>}
            </div>

            <div>
              <label htmlFor="terminal-mac" className="block text-sm font-medium text-gray-700 mb-1">
                MAC Address
              </label>
              <input
                id="terminal-mac"
                type="text"
                value={formData.macAddress}
                onChange={(e) => setFormData({ ...formData, macAddress: e.target.value.toUpperCase() })}
                className={`input w-full font-mono text-sm ${errors.macAddress ? 'border-red-500' : ''}`}
                placeholder="AA:BB:CC:DD:EE:FF"
              />
              {errors.macAddress && <p className="text-red-500 text-xs mt-1">{errors.macAddress}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="terminal-version" className="block text-sm font-medium text-gray-700 mb-1">
                Software Version
              </label>
              <input
                id="terminal-version"
                type="text"
                value={formData.appVersion}
                onChange={(e) => setFormData({ ...formData, appVersion: e.target.value })}
                className="input w-full"
                placeholder="1.0.0"
              />
            </div>

            <div>
              <label htmlFor="terminal-hardware" className="block text-sm font-medium text-gray-700 mb-1">
                Hardware Model
              </label>
              <input
                id="terminal-hardware"
                type="text"
                value={formData.deviceModel}
                onChange={(e) => setFormData({ ...formData, deviceModel: e.target.value })}
                className="input w-full"
                placeholder="e.g., iPad Pro 12.9, Surface Pro"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
            <strong>Note:</strong> The terminal will appear as &quot;Offline&quot; until it connects
            and sends its first heartbeat.
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? 'Registering...' : 'Register Terminal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
